// The chain by tier and area, as the grouped and the matrix arrangements of
// the canvas draw it. A card's tier is the fewest steps to it from the root;
// its area is the curated row of rank l2 its class sits under and its
// branch the row of rank l1, all three as the read gives them. Pure: it
// knows nothing of React or of the canvas.
//
// A card of rank l1 ("Modules & components") names no kind of thing: it is
// left out, and what it rests on is said apart. A card of rank l2 or group,
// which stands for products filed above any kind, is drawn last in its area
// as that area's "not further specified" card.

import {
  ChainCard,
  ChainColumn,
  ChainEdge,
  ChainSide,
  ValueChain,
} from "../../types/valueChain";
import { CardGroup, ColumnGrouping, cardName } from "./chainModel";

// The rank of a curated row that is a branch: a card of it is left out
export const BRANCH_RANK = "l1";

// The ranks of a card that stands for products filed above any kind
const BROAD_RANKS: readonly string[] = ["l2", "group"];

// The order of the areas by the nature their branch holds: the levels of
// the backbone from the largest down, then what sits beside them
export const NATURE_ORDER: readonly string[] = [
  "infrastructure",
  "platform",
  "equipment",
  "module",
  "part",
  "material",
  "software",
  "service",
];

// The area of a card the read places under no curated row
export const NO_AREA = "area:none";
export const NO_AREA_NAME = "Classes found in articles";

// A card left out of a tier view, with the pairs it rests on
export interface LeftOut {
  card: ChainCard;
  statements: number;
}

// What a card rests on: the pairs stated into it from the cards nearer the
// root, every way
export const statementsOf = (card: ChainCard): number =>
  card.paths.reduce((total, path) => total + path.pairs, 0);

// Whether a card stands for products filed above any kind
export const isBroad = (card: ChainCard): boolean =>
  card.type === "class" &&
  card.unplaced &&
  card.rank !== null &&
  BROAD_RANKS.includes(card.rank);

// Whether a card names no kind of thing: a row of rank l1, never the root
const isBranch = (card: ChainCard): boolean =>
  card.side !== null && card.rank === BRANCH_RANK;

const areaKeyOf = (card: ChainCard): string => card.area?.id ?? NO_AREA;

const areaNameOf = (card: ChainCard): string =>
  card.area ? (card.area.name ?? card.area.id) : NO_AREA_NAME;

// The cards of an area in order: the most statements first, then the most
// products, then by name; the broad cards last
export const byEvidence = (a: ChainCard, b: ChainCard): number =>
  Number(isBroad(a)) - Number(isBroad(b)) ||
  statementsOf(b) - statementsOf(a) ||
  b.product_count - a.product_count ||
  cardName(a).localeCompare(cardName(b));

// The side an edge is on: that of its card further from the root
const sideOfEdge = (
  edge: ChainEdge,
  sides: ReadonlyMap<string, ChainSide | null>,
): ChainSide => (sides.get(edge.part) === "parts" ? "parts" : "hosts");

// The read as a tier view takes it: each card in the column of its tier,
// the cards of rank l1 left out, and only the edges from one tier to the
// next. An edge within a tier or back towards the root stays on record (the
// cards still name the hosts they are stated inside) but is not drawn.
export const tierRead = (chain: ValueChain): ValueChain => {
  const cards = chain.cards
    .filter((card) => !isBranch(card))
    .map((card) => ({ ...card, step: card.tier }));
  const tiers = new Map<string, number>([
    [chain.root.id, 0],
    ...cards.map((card): [string, number] => [card.id, card.tier]),
  ]);
  const sides = new Map<string, ChainSide | null>([
    [chain.root.id, null],
    ...cards.map((card): [string, ChainSide | null] => [card.id, card.side]),
  ]);
  const nextTier = (edge: ChainEdge): boolean => {
    const host = tiers.get(edge.host);
    const part = tiers.get(edge.part);
    if (host === undefined || part === undefined) {
      return false;
    }
    return sideOfEdge(edge, sides) === "parts" ? part === host + 1 : host === part + 1;
  };

  const columns = new Map<string, ChainColumn>();
  for (const card of [...cards].sort(byEvidence)) {
    const side = card.side ?? "parts";
    const key = `${side}:${card.tier}`;
    const column = columns.get(key) ?? {
      step: card.tier,
      side,
      cards: [],
      unplaced: [],
      natures: {},
    };
    column.cards.push(card.id);
    if (card.type === "kind" && card.nature) {
      column.natures[card.nature] = (column.natures[card.nature] ?? 0) + 1;
    }
    columns.set(key, column);
  }

  return {
    ...chain,
    cards,
    columns: Array.from(columns.values()).sort(
      (a, b) =>
        Number(a.side === "hosts") - Number(b.side === "hosts") || a.step - b.step,
    ),
    edges: chain.edges.filter(nextTier).map((edge) => ({ ...edge, span: 1 })),
    broken_edges: chain.broken_edges.filter(nextTier),
  };
};

// The grouping of a tier view's columns by area. The areas are ordered over
// the whole chain, so that a matrix gives each one band: by the level of
// their branch's nature, then by their statements; within an area the cards
// go by evidence.
export const areaGrouping = (cards: Iterable<ChainCard>): ColumnGrouping => {
  const totals = new Map<
    string,
    { name: string; level: number; statements: number }
  >();
  for (const card of Array.from(cards)) {
    if (isBranch(card) || card.side === null) {
      continue;
    }
    const key = areaKeyOf(card);
    const level = NATURE_ORDER.indexOf(card.nature ?? "");
    const total = totals.get(key) ?? {
      name: areaNameOf(card),
      level: card.area === null ? NATURE_ORDER.length + 1 : level < 0 ? NATURE_ORDER.length : level,
      statements: 0,
    };
    total.statements += statementsOf(card);
    totals.set(key, total);
  }
  const order = new Map(
    Array.from(totals.entries())
      .sort(
        ([keyA, a], [keyB, b]) =>
          a.level - b.level ||
          b.statements - a.statements ||
          a.name.localeCompare(b.name) ||
          keyA.localeCompare(keyB),
      )
      .map(([key], index) => [key, index]),
  );

  return (columnCards) => {
    const groups = new Map<string, CardGroup>();
    for (const card of columnCards) {
      const key = areaKeyOf(card);
      const group = groups.get(key) ?? {
        key,
        name: areaNameOf(card),
        nature: card.nature,
        order: order.get(key) ?? order.size,
        cards: [],
      };
      group.cards.push(card);
      groups.set(key, group);
    }
    return Array.from(groups.values())
      .map((group) => ({ ...group, cards: [...group.cards].sort(byEvidence) }))
      .sort((a, b) => a.order - b.order);
  };
};

// The cards of rank l1 a read holds, the most statements first
export const leftOutOf = (chain: ValueChain): LeftOut[] =>
  chain.cards
    .filter(isBranch)
    .map((card) => ({ card, statements: statementsOf(card) }))
    .sort((a, b) => b.statements - a.statements);

// The words of the cards left out: the statements each rests on
export const leftOutNote = (leftOut: readonly LeftOut[]): string | null => {
  if (leftOut.length === 0) {
    return null;
  }
  const parts = leftOut.map(
    ({ card, statements }) =>
      `${statements} ${statements === 1 ? "statement" : "statements"} on ${
        card.side === "hosts" ? "hosts" : "parts"
      } filed only as ${cardName(card)}`,
  );
  return `Not shown: ${parts.join("; ")}. A top-level category names no kind of thing.`;
};
